import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export type ColumnDefinition = {
  title: string; // Column header in the Excel file
  key: string; // Key from the data object to extract the value
};

type Status = 'success' | 'error';

/**
 * Exports an array of data objects to an Excel file using specified columns.
 *
 * @param columns - An array of objects defining the Excel column headers and corresponding data keys.
 * @param data - An array of data objects to export.
 * @param fileName - The name of the resulting Excel file (without extension).
 */
export function exportToExcel<T extends Record<string, any>>(
  columns: ColumnDefinition[],
  data: T[],
  fileName: string,
): {
  message: string;
  status: Status;
} {
  try {
    if (!data || data.length === 0) {
      return {
        message: 'No hay datos para exportar',
        status: 'error',
      };
    }
    // Transform each data object to match the selected columns
    const mappedData = data.map((item) => {
      const row: Record<string, any> = {};
      columns.forEach((col) => {
        row[col.title] = item[col.key]; // Use column title as Excel header
      });
      return row;
    });

    // Convert JSON data to a worksheet
    const worksheet = XLSX.utils.json_to_sheet(mappedData);

    // Auto-width calculation per column
    worksheet['!cols'] = columns.map((col) => {
      const headerLength = col.title.length;
      const maxContentLength = Math.max(
        ...data.map((item) => {
          const value = item[col.key];
          return value ? value.toString().length : 0;
        }),
      );
      const adjusted = Math.max(headerLength, maxContentLength) + 2;
      return { wch: adjusted > 30 ? 30 : adjusted }; // Limit width to 30
    });

    // Create a new workbook and append the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

    // Generate an Excel file buffer
    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    // Create a Blob from the buffer and trigger download
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    // Use FileSaver to download the file
    saveAs(blob, `${fileName}.xlsx`);
    return {
      message: `El archivo "${fileName}.xlsx" ha sido exportado exitosamente`,
      status: 'success',
    };
  } catch (_error) {
    return {
      message: 'Error al exportar el archivo',
      status: 'error',
    };
  }
}
